import type { InvalidSubmissionContext, Path } from "vee-validate";
import { useSnackStore } from "@/stores/snackStore.ts";
import { toCamelCase } from "@/utils/Formatters.ts";
import type { useI18n } from "vue-i18n";

export const enum EFormType {
  KEY = "_formType",
  FORM_PRIMARY = "form",
  FORM_SECONDARY = "form-secondary",
}

function formatFieldMessage(raw: string): string {
  const sanitized = raw.replace(/[\[\]]/g, "_");

  return sanitized
    .split(/[._]+/)
    .map((seg) => {
      if (/^\d+$/.test(seg)) {
        return `${Number(seg) + 1}:`;
      }
      return seg.charAt(0).toUpperCase() + seg.slice(1);
    })
    .join(" ");
}

function flattenErrors<TInput>(
  errors?: Partial<Record<Path<TInput>, string | Record<string, any>>>
): string[] {
  if (!errors) {
    return [];
  }

  const messages = Object.entries(errors)
    .filter(([, msg]) => {
      if (typeof msg === "object" && msg !== null) {
        return Object.keys(msg).length > 0;
      }
      return true;
    })
    .map(([path, msg]) => {
      if (typeof msg === "string" && msg.trim() !== "") {
        return formatFieldMessage(msg);
      }
      return formatFieldMessage(path);
    });
  return [...new Set(messages)];
}

export type HandleControlledSubmitOptions = {
  /** When false, only scrolls to the first error. Defaults to true. */
  showSnackbar?: boolean;
};

export function handleControlledSubmit(
  context: Partial<InvalidSubmissionContext>,
  { showSnackbar = true }: HandleControlledSubmitOptions = {}
) {
  if (context.errors && Object.values(context.errors).length) {
    if (showSnackbar) {
      const snackStore = useSnackStore();
      const errors = flattenErrors(context.errors);

      if (errors.length > 3) {
        // Fallback to English if i18n is not available
        snackStore.showError("Some fields are required");
      } else {
        snackStore.showError(errors);
      }
    }
    setTimeout(() => {
      scrollToFirstError();
    }, 100);
  }
}

export function createHandleControlledSubmit(options: HandleControlledSubmitOptions = {}) {
  return (context: Partial<InvalidSubmissionContext>) => handleControlledSubmit(context, options);
}

export function scrollToFirstError() {
  const errors = Array.from(
    document.querySelectorAll(String.raw`.v-field--error, .tw\:text-error, [data-field-error]`)
  );

  if (errors.length > 0 && errors[0] instanceof HTMLElement) {
    errors[0].scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

export function copyToClipboard(value: string | null | undefined): void {
  if (value) {
    navigator.clipboard.writeText(value).then(() => {
      useSnackStore().showSuccess("Copied to clipboard!");
    });
  }
}

export function toDotBracketPath(path: string) {
  return path.replace(/\.(\d+)(?=\.|$)/g, (_, n) => `[${n}]`);
}

type TryRunResult<T> = { ok: true; value: T } | { ok: false; value: undefined };

export async function tryRun<T>(fn: () => Promise<T> | T): Promise<TryRunResult<T>>;
export async function tryRun<T>(
  fn: () => Promise<T> | T,
  onSuccess: (value: T) => Promise<void> | void
): Promise<{ ok: boolean }>;
export async function tryRun<T>(
  fn: () => Promise<T> | T,
  onSuccess?: (value: T) => Promise<void> | void
): Promise<TryRunResult<T> | { ok: boolean }> {
  let value: T;
  try {
    value = await fn();
  } catch {
    return { ok: false, value: undefined };
  }
  if (onSuccess) {
    await onSuccess(value);
    return { ok: true };
  }
  return { ok: true, value };
}

export function cleanErrorMessage(message: string, t: ReturnType<typeof useI18n>["t"]) {
  const separator = " is ";
  const separatorIndex = message.indexOf(separator);

  // Only rewrite "path is …" messages from vee/yup. Plain prose (e.g. URL hints with
  // "domain.com") must not be treated as a field path just because it contains a dot.
  if (separatorIndex === -1) {
    return message.replace(/\\\./g, ".");
  }

  const firstPart = message.slice(0, separatorIndex);

  if (!firstPart) {
    return message;
  }

  const looksLikePath =
    /^(?:[\w]+(?:\[\d+\])?)(?:\.[\w]+(?:\[\d+\])?)+$/.test(firstPart) ||
    /^[\w]+\[\d+\]$/.test(firstPart);

  if (!looksLikePath) {
    return message.replace(/\\\./g, ".");
  }

  const lastKey = firstPart
    .replace(/\[\d+\]/g, "")
    .split(".")
    .at(-1);

  if (!lastKey) {
    return message;
  }

  const translatedField = t(`generic.fields.${toCamelCase(lastKey)}`);

  return message.replace(firstPart, translatedField);
}

/**
 * Coerce null/undefined form values to Django-friendly empties before submit.
 *
 * Use for fields that are `blank=True` but not `null=True` (CharField → `""`,
 * ArrayField → `[]`). Nullable fields should be omitted from the key lists.
 */
export function coerceBlankFields<T extends object>(
  values: T,
  {
    toEmptyString = [],
    toEmptyArray = [],
  }: {
    toEmptyString?: readonly (keyof T & string)[];
    toEmptyArray?: readonly (keyof T & string)[];
  } = {}
): T {
  const payload = { ...(values as Record<string, unknown>) };

  for (const key of toEmptyString) {
    if (payload[key] == null) {
      payload[key] = "";
    }
  }

  for (const key of toEmptyArray) {
    if (!Array.isArray(payload[key])) {
      payload[key] = [];
    }
  }

  return payload as T;
}
